import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { RadarAvatar, RadarCrest, RadarLabelPill } from "./RadarAtoms";
import RadarLabelEditor from "./RadarLabelEditor";
import RadarNotes from "./RadarNotes";
import { agentRadarService } from "../../services/agentRadarService";
import { radarMetaLine, formatMonthYear, formatDayMonth } from "./radarFormat";

// Entry detail — desktop 430px right slide-over, mobile near-full bottom sheet.
// Labels come LIVE from the entry object (the hook keeps card + detail in sync), so
// assign/unassign repaint both. Lists membership + notes are fetched on open. The
// label create/assign and note composer reuse RadarLabelEditor / RadarNotes.
const EntryDetail = ({
  entry,
  mobile = false,
  lists = [],
  ownerLabels = [],
  onClose,
  onOpenProfile,
  onAssignLabel,
  onUnassignLabel,
  onCreateLabel,
  onAddNote,
}) => {
  const [notes, setNotes] = useState([]);
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  const labelWrapRef = useRef(null);

  useEffect(() => {
    if (!entry) return undefined;
    let alive = true;
    const controller = new AbortController();
    (async () => {
      // Detail is only needed for the note BODIES (list serializer omits them).
      // Everything else on the header/labels/lists comes from the live list entry.
      const res = await agentRadarService.getEntry(entry.id, controller.signal).catch(() => null);
      if (!alive || !res || !res.success) return;
      setNotes(res.data.notes || []);
    })();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [entry]);

  useEffect(() => {
    if (!labelMenuOpen || mobile) return undefined;
    const onDown = (e) => {
      if (labelWrapRef.current && !labelWrapRef.current.contains(e.target)) setLabelMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [labelMenuOpen, mobile]);

  if (!entry) return null;

  const entryLabels = entry.labels || [];
  const availableLabels = ownerLabels.filter((l) => !entryLabels.some((el) => el.id === l.id));
  const contract = formatMonthYear(entry.contract_until);

  // Detail returns only list_ids — map to names from the lists prop (single source).
  const listNames = (entry.list_ids || []).map((id) => lists.find((l) => l.id === id)?.name).filter(Boolean);
  const addedAt = entry.added_at;

  const handleCreate = async ({ name, color }) => {
    const res = await onCreateLabel({ name, color });
    if (res?.success && res.data) {
      onAssignLabel(entry.id, res.data);
      setLabelMenuOpen(false);
    }
    return res;
  };

  const handleAssign = (label) => {
    onAssignLabel(entry.id, label);
    setLabelMenuOpen(false);
  };

  const handleAddNote = async (text) => {
    const res = await onAddNote(entry.id, text);
    if (res?.success && res.data) setNotes((prev) => [res.data, ...prev]);
    return res;
  };

  const notesTitle = notes.length > 0 ? `Notes (${notes.length})` : "Notes";

  const header = (
    <>
      <RadarAvatar name={entry.name} imageUrl={entry.player_image_url} size={mobile ? 48 : 52} fontSize={mobile ? 15 : 16} seed={entry.id} />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[#111827]" style={{ fontSize: mobile ? 15.5 : 15 }}>{entry.name}</div>
        <div className="text-[11.5px] text-[#6B7280]" style={{ marginTop: 1 }}>{radarMetaLine(entry)}</div>
        <div className="flex items-center" style={{ gap: 6, marginTop: 6 }}>
          <RadarCrest name={entry.club_name} logoUrl={entry.club_logo_url} size={16} seed={entry.id} />
          <div className="text-[12px] text-[#374151] font-semibold truncate">{entry.club_name}</div>
          {contract && <div className="text-[11px] text-[#9CA3AF] flex-shrink-0">· until {contract}</div>}
        </div>
        <button
          type="button"
          onClick={() => onOpenProfile(entry)}
          className="bg-transparent border-none text-[#2283FF] font-semibold cursor-pointer"
          style={{ fontSize: mobile ? 12.5 : 12, padding: mobile ? "8px 0 0" : 0, marginTop: mobile ? 0 : 8 }}
        >
          View full profile ›
        </button>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="border-none bg-transparent text-[#9CA3AF] hover:text-[#374151] cursor-pointer leading-none"
        style={mobile ? { width: 44, height: 44, margin: "-6px -10px 0 0", fontSize: 16, padding: 0 } : { fontSize: 17, padding: 2 }}
      >
        ✕
      </button>
    </>
  );

  const body = (
    <>
      <div className="text-[11px] text-[#9CA3AF]">
        In lists: <span className="text-[#6B7280] font-semibold">{listNames.join(" · ")}</span>
        {addedAt && <> · Added {formatDayMonth(addedAt)}</>}
      </div>

      {/* labels */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]" style={{ marginBottom: 8 }}>Labels</div>
        <div className="flex items-center flex-wrap relative" style={{ gap: mobile ? 6 : 5 }} ref={labelWrapRef}>
          {entryLabels.map((lb) => (
            <RadarLabelPill key={lb.id} label={lb} onRemove={() => onUnassignLabel(entry.id, lb)} />
          ))}
          <span className="relative">
            <button
              type="button"
              onClick={() => setLabelMenuOpen((v) => !v)}
              className="inline-flex items-center whitespace-nowrap cursor-pointer"
              style={{ border: "1px dashed #C7D0DC", background: "#FFFFFF", color: "#6B7280", borderRadius: 6, padding: mobile ? "4px 10px" : "1px 8px", fontSize: mobile ? 11.5 : 11, fontWeight: 600, lineHeight: "18px" }}
            >
              + Label
            </button>
            {labelMenuOpen && !mobile && (
              <div
                className="absolute z-30"
                style={{ left: 0, top: 26, width: 212, background: "#FFFFFF", border: "1px solid #E4E9F0", borderRadius: 9, boxShadow: "0 12px 32px rgba(17,24,39,0.14)", padding: 6 }}
                onClick={(e) => e.stopPropagation()}
              >
                <RadarLabelEditor availableLabels={availableLabels} onAssign={handleAssign} onCreate={handleCreate} />
              </div>
            )}
          </span>
        </div>
        {labelMenuOpen && mobile && (
          <div style={{ marginTop: 10, background: "#F8FAFC", border: "1px solid #EEF1F5", borderRadius: 11, padding: 8 }}>
            <RadarLabelEditor availableLabels={availableLabels} onAssign={handleAssign} onCreate={handleCreate} />
          </div>
        )}
      </div>

      {/* notes */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]" style={{ marginBottom: 8 }}>{notesTitle}</div>
        <RadarNotes notes={notes} onAdd={handleAddNote} mobile={mobile} />
      </div>
    </>
  );

  if (mobile) {
    return createPortal(
      <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
        <div className="absolute inset-0 bg-[rgba(17,24,39,0.28)]" onClick={onClose} aria-hidden="true" />
        <div
          className="absolute left-0 right-0 bottom-0 bg-white flex flex-col"
          style={{ top: 64, borderRadius: "18px 18px 0 0", boxShadow: "0 -12px 32px rgba(17,24,39,0.14)" }}
        >
          <div onClick={onClose} className="cursor-pointer" style={{ padding: "8px 0 2px" }}>
            <div className="w-9 h-1 rounded-full bg-[#DDE3EB] mx-auto" aria-hidden="true" />
          </div>
          <div className="flex items-start" style={{ gap: 12, padding: "8px 18px 12px", borderBottom: "1px solid #EEF1F5" }}>
            {header}
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col" style={{ gap: 18, padding: "14px 18px 40px" }}>
            {body}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <>
      <div className="fixed inset-0 bg-[rgba(17,24,39,0.28)] z-[55]" onClick={onClose} aria-hidden="true" />
      <div
        className="fixed top-0 right-0 bottom-0 bg-white flex flex-col z-[60]"
        style={{ width: 430, maxWidth: "92vw", borderLeft: "1px solid #E4E9F0", boxShadow: "-16px 0 40px rgba(17,24,39,0.12)" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start" style={{ gap: 12, padding: "18px 20px 14px", borderBottom: "1px solid #EEF1F5" }}>
          {header}
        </div>
        <div className="flex-1 overflow-y-auto flex flex-col" style={{ gap: 18, padding: "16px 20px 24px" }}>
          {body}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default EntryDetail;
